import React, { useEffect, useRef, useState } from 'react'
import { DateRange } from 'react-date-range'
import { FaRegCalendar } from 'react-icons/fa'
import './SelectDatePicker.scss'
import { format } from 'date-fns'
import { Form } from 'react-bootstrap'

/**
 * SelectDatePicker Component
 *
 * A customizable date picker component that allows users to select a date from a calendar popup.
 *
 * @param {Object} props - Component properties
 * @param {string} props.selectedDate - Currently selected date in string format
 * @param {Function} props.onDateChange - Callback function to handle date selection
 * @param {string} [props.minDate='yyyy-MM-dd'] - Minimum selectable date
 * @param {string} [props.maxDate='yyyy-MM-dd'] - Maximum selectable date
 * @param {string} [props.inputClassName='form-control'] - CSS class for the input field
 * @param {boolean} [props.shouldShiftLeft=false] - Whether to shift the calendar left
 * @param {string} [props.dateFormat='dd-MM-yyyy'] - Format for the input placeholder
 * @param {string} [props.returnDateFormat='yyyy-MM-dd'] - Format for the selected date output
 * @returns {JSX.Element} React component
 */
const SelectDatePicker = ({
  selectedDate,
  onDateChange,
  minDate,
  maxDate,
  inputClassName = 'form-control',
  shouldShiftLeft = false,
  dateFormat = 'dd-MM-yyyy',
  returnDateFormat = 'yyyy-MM-dd'
}) => {
  const [showCalendar, setShowCalendar] = useState(false)
  const pickerRef = useRef(null)

  // Handle clicks outside the calendar component to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowCalendar(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle date selection and format the selected date
  const handleSelect = (ranges) => {
    const selected = ranges.selection.startDate
    const formattedDate = format(selected, returnDateFormat)
    onDateChange(formattedDate)
    setShowCalendar(false)
  }

  // Convert minDate and maxDate props to Date objects
  const minDateObj = minDate ? new Date(minDate) : undefined
  const maxDateObj = maxDate ? new Date(maxDate) : undefined

  // Parse the selected date and format it for display in the input field
  const parsedSelectedDate = selectedDate ? new Date(selectedDate) : new Date()
  const formattedSelectedDate = format(parsedSelectedDate, dateFormat)

  return (
    <div className='date-picker-container' ref={pickerRef}>
      <div className='date-input-wrapper'>
        <Form.Control
          type='text'
          className={inputClassName}
          value={formattedSelectedDate}
          placeholder={dateFormat}
          readOnly
          onClick={() => setShowCalendar(!showCalendar)}
        />
        <FaRegCalendar className='calendar-icon' onClick={() => setShowCalendar(!showCalendar)} />
      </div>

      {showCalendar && (
        <div className={`date-picker-popup ${shouldShiftLeft ? 'left-adjust' : ''}`}>
          <DateRange
            ranges={[{ startDate: parsedSelectedDate, endDate: parsedSelectedDate, key: 'selection' }]}
            onChange={handleSelect}
            minDate={minDateObj}
            maxDate={maxDateObj}
            showSelectionPreview={false}
            moveRangeOnFirstSelection={false}
            showMonthAndYearPickers
            showDateDisplay={false}
            editableDateInputs={false}
            dragSelectionEnabled={false}
          />
        </div>
      )}
    </div>
  )
}

export default SelectDatePicker
